/**
 * User Team Hooks - React Query hooks for user team operations
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getUserTeams,
  addUserTeam,
  updateUserTeam,
  removeUserTeam,
  checkAffectedTeamOKRs,
  AddTeamMembershipRequest,
  UpdateTeamMembershipRequest,
  AffectedTeamOKRsResult,
} from '../services/userTeams';
import toast from 'react-hot-toast';
import { getApiErrorMessage } from '../utils/apiError';

// Re-export types for convenience
export type { AffectedTeamOKRsResult };

// Re-export checkAffectedTeamOKRs for direct use in components
export { checkAffectedTeamOKRs };

// Query keys
export const userTeamKeys = {
  all: ['userTeams'] as const,
  user: (userId: string) => [...userTeamKeys.all, userId] as const,
};

/**
 * Get user's team memberships
 */
export const useUserTeams = (userId: string) => {
  return useQuery({
    queryKey: userTeamKeys.user(userId),
    queryFn: () => getUserTeams(userId),
    enabled: !!userId,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
};

/**
 * Add user to a team
 */
export const useAddUserTeam = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: AddTeamMembershipRequest }) =>
      addUserTeam(userId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: userTeamKeys.user(variables.userId) });
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      toast.success('User added to team successfully');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, 'Failed to add user to team'));
    },
  });
};

/**
 * Update user's team membership (role)
 */
export const useUpdateUserTeam = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      teamId,
      data,
    }: {
      userId: string;
      teamId: string;
      data: UpdateTeamMembershipRequest;
    }) => updateUserTeam(userId, teamId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: userTeamKeys.user(variables.userId) });
      toast.success('Team role updated successfully');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, 'Failed to update team membership'));
    },
  });
};

/**
 * Remove user from a team
 */
export const useRemoveUserTeam = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, teamId, force = false }: { userId: string; teamId: string; force?: boolean }) =>
      removeUserTeam(userId, teamId, force),
    onSuccess: (result, variables) => {
      queryClient.invalidateQueries({ queryKey: userTeamKeys.user(variables.userId) });
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      if (result.clearedOKRs > 0) {
        queryClient.invalidateQueries({ queryKey: ['okrs'] });
        toast.success(`Team removed. Team association cleared from ${result.clearedOKRs} OKR(s).`);
      } else {
        toast.success('User removed from team successfully');
      }
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, 'Failed to remove user from team'));
    },
  });
};
